/* clickable-fit-predicate.js — THE fitment-matching predicate.
 *
 * LOCKSTEP FAMILY, FOURTH MEMBER (Lucas ruling, 21 Aug 2026 — the
 * exact-reservation build). The single implementation of the vehicle
 * comparison the whole theme matches with:
 *   - sameVehicle: case-insensitive full-string equality (the Build
 *     Reference fitment contract — never partial, never fuzzy);
 *   - hasVehicleTag: tag-membership by that equality (fitVerdict's core);
 *   - firstMatchIndex / entryAt: the parallel-metafield row match
 *     (index i across model/warning/helpful is one entry).
 *
 * THREE CALLERS, ZERO OTHER COPIES (grep-verified at ship):
 *   1. clickableVehicleState.fitVerdict — KEEPS its Tier-0 choke-point
 *      role and public surface; delegates its comparison here.
 *   2. clickable-card-fitment.js / clickable-fitment.js — row match +
 *      message pick for the paint surfaces; local copies deleted.
 *   3. window.ckFitStamp (below) — the PRE-PAINT stamp that marks the
 *      matching latent chip per card so CSS can lay out exactly it.
 *
 * LOADED SYNC in the head (theme.liquid, beside the vehicle guard) —
 * parser-blocking by design (~1.3 KB): the stamp must exist before the
 * first card parses. FAIL CLOSED everywhere: if this file is absent or
 * errors, nothing is marked and every consumer's guard paints the silent
 * baseline (rule 27) — broken looks like nothing.
 *
 * VERSION LOCKSTEP: any change to the comparison here must be reflected
 * in scripts/generate-storefront-data.py (veh_re), the head guard
 * (FULL_MODEL_RE family) and clickable-vehicle-state.js in the same
 * commit — and vice versa. See CLAUDE.md "Data contracts".
 */
(function () {
  'use strict';
  if (window.clickableFitPredicate) return; /* run-once (rule 21) */

  function sameVehicle(a, b) {
    return typeof a === 'string' && typeof b === 'string' &&
      a.toLowerCase() === b.toLowerCase();
  }

  function hasVehicleTag(tags, name) {
    if (!Array.isArray(tags) || typeof name !== 'string') return false;
    for (var i = 0; i < tags.length; i++) {
      if (sameVehicle(tags[i], name)) return true;
    }
    return false;
  }

  function firstMatchIndex(models, name) {
    if (!Array.isArray(models) || typeof name !== 'string') return -1;
    for (var i = 0; i < models.length; i++) {
      if (sameVehicle(models[i], name)) return i;
    }
    return -1;
  }

  function entryAt(list, index) {
    if (!Array.isArray(list)) return '';
    var value = list[index];
    return typeof value === 'string' ? value.trim() : '';
  }

  window.clickableFitPredicate = {
    sameVehicle: sameVehicle,
    hasVehicleTag: hasVehicleTag,
    firstMatchIndex: firstMatchIndex,
    entryAt: entryAt
  };

  /* ---- PRE-PAINT CHIP STAMP ------------------------------------------
     Called from a tiny inline <script> rendered at the END of each card
     (and after the PDP fitment-data block): marks data-ck-match on the
     latent chip whose text is the matching row's message, using ONLY the
     shared predicate above + the guard's verified-level test. CSS lays
     out marked chips at their own height and drops unmatched latents
     from layout entirely — reservation == revealed pill, exactly.
     Every failure path returns without marking = silent baseline. */
  function verifiedVehicleName() {
    try {
      var name = localStorage.getItem('activeVehicleName');
      if (name === null) return null;
      name = name.replace(/"/g, '');
      if (!window.clickableVehicleLevel) return null; /* guard absent: closed */
      return window.clickableVehicleLevel(name) === 'VERIFIED' ? name : null;
    } catch (error) { return null; }
  }

  function parseJsonAttr(el, attr) {
    var raw = el && el.getAttribute(attr);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch (error) { return null; }
  }

  function markIn(host, warning, helpful) {
    var chips = host.querySelectorAll('.ck-latent-stack > [data-ck-latent]');
    for (var i = 0; i < chips.length; i++) {
      var chip = chips[i];
      var isWarn = chip.className.indexOf('warn') !== -1;
      var want = isWarn ? warning : helpful;
      var text = (chip.textContent || '').trim();
      if (want && text === want) chip.setAttribute('data-ck-match', '');
    }
  }

  /* PRE-PAINT RED STAMP (b117 item 1): the PDP vehicle pill's runtime owner
     (clickable-fitment.js paintRedBand) HIDES the pill on a red verdict and
     lets the red band replace it. That verdict arrives deferred, so the pill
     would paint for a frame on a non-fitting product once the face is
     stamp-decided. Called from a tiny inline script rendered BEFORE the
     apps-section markup with the product's tags island beside it; stamps
     html[data-ck-fit="red"] using ONLY the shared predicate + the guard's
     level test. fitment.js syncs the same attribute at runtime. Every
     failure path returns without marking = silent baseline (pill shows,
     fitment.js rules as before). */
  window.ckRedStamp = function (scriptEl) {
    try {
      var name = verifiedVehicleName();
      if (!name || !scriptEl || !scriptEl.previousElementSibling) return;
      var island = scriptEl.previousElementSibling;
      if (!island.hasAttribute('data-ck-pdp-tags')) return;
      var tags = JSON.parse(island.textContent);
      if (!hasVehicleTag(tags, name)) document.documentElement.setAttribute('data-ck-fit', 'red');
    } catch (error) { /* silent baseline */ }
  };

  window.ckFitStamp = function (scriptEl) {
    try {
      var name = verifiedVehicleName();
      if (!name || !scriptEl) return;
      var card = scriptEl.closest ? scriptEl.closest('[data-product-card]') : null;
      if (card) {
        var tags = parseJsonAttr(card, 'data-vtags');
        if (!hasVehicleTag(tags, name)) return; /* red/generic: never mark */
        var blob = parseJsonAttr(card, 'data-fitment');
        if (!blob) return;
        var index = firstMatchIndex(blob.model, name);
        if (index === -1) return;
        markIn(card, entryAt(blob.warning, index), entryAt(blob.helpful, index));
        return;
      }
      /* PDP: the call sits after [data-fitment-data]; tags + blob are the
         page-level JSON islands, the slot is the single message slot. */
      var root = scriptEl.closest ? scriptEl.closest('[data-pdp-root]') : null;
      if (!root) return;
      var tagsEl = root.querySelector('[data-vehicle-tags]');
      var dataEl = root.querySelector('[data-fitment-data]');
      var slot = root.querySelector('[data-message-slot]');
      if (!tagsEl || !dataEl || !slot) return;
      var ptags, pblob;
      try { ptags = JSON.parse(tagsEl.textContent); pblob = JSON.parse(dataEl.textContent); }
      catch (error) { return; }
      if (!hasVehicleTag(ptags, name)) return;
      var pindex = firstMatchIndex(pblob.model, name);
      if (pindex === -1) return;
      markIn(slot, entryAt(pblob.warning, pindex), entryAt(pblob.helpful, pindex));
    } catch (error) { /* closed: unmarked = silent */ }
  };
})();
